/**
 * Draw del jugador (maquetas aprobadas 2026-10-16: Jugador-Draw-RR-cel,
 * Jugador-Draw-Elim-cel y Jugador-Draw): «Mi grupo», «Mi camino» y el rol de
 * juegos, derivados SOLO de lo que el jugador ya recibe (partidos de
 * /player/tournaments/:id/results y la tabla de /player/.../standings).
 *
 * Privacidad: el servidor vacía fecha, hora y cancha de los partidos con
 * horario sin publicar (`schedule_pending`); aquí además nunca se arma un
 * horario de un partido así: dice «Horario por confirmar».
 */

import type { BracketRound } from "./bracketLayout";
import type { MatchParticipantBrief, StandingsGroup, StandingsRow, StandingsTiebreaker, TournamentMatch } from "./api";
import { matchParticipantShortName } from "./api";
import { dropMatchState } from "./dropMatch";
import { isSchedulePendingForPlayer, SCHEDULE_PENDING_LABEL } from "./matchScheduleStatus";
import { posterDay, posterTime } from "./drawPoster";
import { rrCourtLong, rrCourtShort } from "./rrMatrix";
import { scoreTextFromWinner } from "./scoreFromWinner";
import { tiebreakerSentence } from "./standingsRules";

/** «Jornada 3» → 3 (null si la etiqueta no trae número). */
export function jornadaNumber(roundLabel: string | null | undefined): number | null {
  const found = /(\d+)/.exec(roundLabel ?? "");
  return found ? Number(found[1]) : null;
}

export function isTieMatch(match: TournamentMatch): boolean {
  return match.match_kind === "tie";
}

function isCancelled(match: TournamentMatch): boolean {
  return String(match.status) === "cancelled";
}

function hasParticipant(match: TournamentMatch, participantId: number | null | undefined): boolean {
  if (participantId == null) return false;
  return match.player1_participant_id === participantId || match.player2_participant_id === participantId;
}

/** Partido decidido (tiene ganador). */
export function isDecided(match: TournamentMatch): boolean {
  return match.winner_participant_id != null;
}

/** Partido que sigue por jugarse (sin ganador, no cancelado, no caído por baja). */
export function isStillToPlay(match: TournamentMatch): boolean {
  if (isDecided(match) || isCancelled(match)) return false;
  const drop = isTieMatch(match) ? null : dropMatchState(match);
  return !drop || drop.kind === "pending";
}

export function opponentOf(match: TournamentMatch, participantId: number): MatchParticipantBrief | null {
  return match.player1_participant_id === participantId ? match.player2 : match.player1;
}

/** Marcador desde la fila (quien está en la fila primero): «6-3 7-6»;
 * serie por equipos: «Serie 2-1». null si no hay marcador. */
export function scoreFromSide(match: TournamentMatch, participantId: number): string | null {
  const fromP1 = match.player1_participant_id === participantId;
  if (isTieMatch(match)) {
    const raw = (match.score_text ?? "").trim();
    const found = raw.match(/^(\d+)\s*-\s*(\d+)$/);
    if (!found) return raw ? `Serie ${raw}` : null;
    return `Serie ${fromP1 ? `${found[1]}-${found[2]}` : `${found[2]}-${found[1]}`}`;
  }
  const sets = match.score_sets ?? [];
  if (sets.length > 0) {
    return sets
      .map((set) => (fromP1 ? `${set.player1_games}-${set.player2_games}` : `${set.player2_games}-${set.player1_games}`))
      .join(" ");
  }
  const text = match.score_text?.trim();
  if (!text) return null;
  // El texto se guarda desde el jugador 1: «desde el ganador» y, si la fila
  // perdió, se voltea otra vez.
  return scoreTextFromWinner(text, fromP1);
}

export interface PlayerWhen {
  /** «sáb 24 oct» (vacío sin fecha). */
  day: string;
  /** «10:30» (vacío sin hora). */
  time: string;
  /** «Cancha 2» (vacío sin cancha). */
  court: string;
  /** El organizador aún no publica el horario. */
  pending: boolean;
}

/** Cuándo se juega, respetando «Horario por confirmar». */
export function playerWhen(match: TournamentMatch): PlayerWhen {
  if (isSchedulePendingForPlayer(match)) return { day: "", time: "", court: "", pending: true };
  return {
    day: posterDay(match.scheduled_date),
    time: posterTime(match.scheduled_time),
    court: rrCourtLong(match.court),
    pending: false,
  };
}

/** «9:00 · C1» para el rol de juegos (o «Horario por confirmar» / «por programar»). */
export function rolWhenText(match: TournamentMatch): string {
  if (isSchedulePendingForPlayer(match)) return SCHEDULE_PENDING_LABEL;
  const text = [posterTime(match.scheduled_time), rrCourtShort(match.court)].filter(Boolean).join(" · ");
  return text || "por programar";
}

/** Texto de un día con mayúscula inicial: «sáb 24 oct» → «Sáb 24 oct». */
export function capitalized(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

// ---- Mi grupo ----------------------------------------------------------

export interface MyResult {
  match: TournamentMatch;
  opponent: MatchParticipantBrief | null;
  opponentNumber: number | null;
  won: boolean;
  score: string | null;
  jornada: number | null;
  day: string;
}

export interface MyGroupSummary {
  position: number | null;
  total: number;
  wins: number | null;
  losses: number | null;
  gamesWon: number | null;
  gamesLost: number | null;
  next: {
    match: TournamentMatch;
    opponent: MatchParticipantBrief | null;
    opponentNumber: number | null;
    jornada: number | null;
    when: PlayerWhen;
  } | null;
  results: MyResult[];
}

function byJornadaThenDate(a: TournamentMatch, b: TournamentMatch): number {
  const ja = jornadaNumber(a.round_label) ?? 1_000;
  const jb = jornadaNumber(b.round_label) ?? 1_000;
  if (ja !== jb) return ja - jb;
  const da = a.scheduled_date ?? "9999";
  const db = b.scheduled_date ?? "9999";
  if (da !== db) return da.localeCompare(db);
  return (a.match_number ?? 1_000_000) - (b.match_number ?? 1_000_000) || a.id - b.id;
}

/**
 * Resumen de «Mi grupo»: lugar, G-P y games salen de la tabla que el jugador
 * ya ve (standings del jugador); si esa tabla no llega, quedan en null y la
 * tarjeta muestra «—». El próximo partido y los resultados, de los partidos.
 */
export function deriveMyGroupSummary(input: {
  matches: TournamentMatch[];
  participantIds: number[];
  myParticipantId: number;
  standings?: StandingsGroup | null;
}): MyGroupSummary {
  const { myParticipantId } = input;
  const numberById = new Map(input.participantIds.map((id, index) => [id, index + 1]));
  const mine = input.matches.filter((match) => hasParticipant(match, myParticipantId)).sort(byJornadaThenDate);
  const row: StandingsRow | null =
    input.standings?.standings.find((item) => item.participant_id === myParticipantId) ?? null;
  const anyPlayed = !!input.standings && input.standings.standings.some((item) => item.matches_played > 0);

  const nextMatch = mine.find(isStillToPlay) ?? null;
  const results: MyResult[] = mine.filter(isDecided).map((match) => {
    const opponent = opponentOf(match, myParticipantId);
    return {
      match,
      opponent,
      opponentNumber: opponent ? numberById.get(opponent.id) ?? null : null,
      won: match.winner_participant_id === myParticipantId,
      score: scoreFromSide(match, myParticipantId),
      jornada: jornadaNumber(match.round_label),
      day: isSchedulePendingForPlayer(match) ? "" : capitalized(posterDay(match.scheduled_date)),
    };
  });

  let next: MyGroupSummary["next"] = null;
  if (nextMatch) {
    const opponent = opponentOf(nextMatch, myParticipantId);
    next = {
      match: nextMatch,
      opponent,
      opponentNumber: opponent ? numberById.get(opponent.id) ?? null : null,
      jornada: jornadaNumber(nextMatch.round_label),
      when: playerWhen(nextMatch),
    };
  }

  return {
    position: row && anyPlayed ? row.position : null,
    total: input.participantIds.length,
    wins: row ? row.wins : null,
    losses: row ? row.losses : null,
    gamesWon: row ? row.games_won : null,
    gamesLost: row ? row.games_lost : null,
    next,
    results,
  };
}

// ---- Rol de juegos -----------------------------------------------------

export interface RolJornada {
  round: number;
  /** Todos sus partidos decididos (o caídos). */
  played: boolean;
  /** La primera jornada con partidos por jugar. */
  isNext: boolean;
  /** «Sáb 24 oct» si todos sus partidos con fecha publicada caen el mismo día. */
  day: string;
  matches: TournamentMatch[];
}

export function buildRolDeJuegos(matches: TournamentMatch[]): RolJornada[] {
  const byRound = new Map<number, TournamentMatch[]>();
  for (const match of matches) {
    if (isCancelled(match)) continue;
    const round = jornadaNumber(match.round_label) ?? 0;
    byRound.set(round, [...(byRound.get(round) ?? []), match]);
  }
  const rounds = [...byRound.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([round, list]) => {
      const sorted = [...list].sort(
        (a, b) =>
          (isSchedulePendingForPlayer(a) ? "99" : a.scheduled_time ?? "99").localeCompare(
            isSchedulePendingForPlayer(b) ? "99" : b.scheduled_time ?? "99",
          ) || (a.match_number ?? 1_000_000) - (b.match_number ?? 1_000_000) || a.id - b.id,
      );
      const days = new Set(
        sorted.filter((match) => !isSchedulePendingForPlayer(match) && match.scheduled_date).map((match) => match.scheduled_date),
      );
      const onlyDay = days.size === 1 ? [...days][0] ?? null : null;
      return {
        round,
        played: sorted.every((match) => !isStillToPlay(match)),
        isNext: false,
        day: onlyDay ? capitalized(posterDay(onlyDay)) : "",
        matches: sorted,
      };
    });
  const nextIndex = rounds.findIndex((round) => !round.played);
  if (nextIndex >= 0) rounds[nextIndex] = { ...rounds[nextIndex], isNext: true };
  return rounds;
}

/** Jornadas que se ven con el rol plegado: `count` alrededor de la próxima
 * (en celular la próxima y la siguiente; en computadora las que llevan a ella). */
export function visibleRolWindow(rounds: RolJornada[], count: number): RolJornada[] {
  if (rounds.length <= count) return rounds;
  const next = rounds.findIndex((round) => round.isNext);
  const anchor = next >= 0 ? next : rounds.length - 1;
  let start = count >= 4 ? anchor - (count - 1) : anchor;
  start = Math.max(0, Math.min(start, rounds.length - count));
  return rounds.slice(start, start + count);
}

// ---- Leyenda: desempate --------------------------------------------------

/** Texto del desempate para la leyenda: el de la categoría si lo define;
 * si no, la regla de siempre del servidor (standings/services.py). */
export function tiebreakLegendText(tiebreakers: StandingsTiebreaker[] | null | undefined): string {
  if (tiebreakers && tiebreakers.length > 0) {
    return `Desempate: partidos ganados, luego ${tiebreakerSentence(tiebreakers)}. Super tiebreak cuenta como 1 game.`;
  }
  return "Desempate: partidos ganados, luego quién le ganó a quién, luego diferencia de sets y de games. Super tiebreak cuenta como 1 game.";
}

/** «Pasan 1º y 2º» / «Pasa el 1º» / «Pasan del 1º al 4º». */
export function qualifyText(count: number | null | undefined): string | null {
  if (!count || count < 1) return null;
  if (count === 1) return "Pasa el 1º";
  if (count === 2) return "Pasan 1º y 2º";
  return `Pasan del 1º al ${count}º`;
}

// ---- Mi camino (eliminatoria) ------------------------------------------

export interface PathStep {
  label: string;
  match: TournamentMatch | null;
  state: "won" | "lost" | "next" | "future";
  opponent: MatchParticipantBrief | null;
  score: string | null;
  when: PlayerWhen | null;
}

export interface MyPath {
  steps: PathStep[];
  /** Ids de los partidos del cuadro en los que estás (para marcarlos en lima). */
  matchIds: Set<number>;
  alive: boolean;
  wins: number;
  /** Ronda donde estás (o donde quedaste). */
  currentLabel: string | null;
}

/** Tu recorrido en el cuadro: rondas ganadas, la que sigue y las que faltan. */
export function deriveMyPath(input: {
  rounds: BracketRound[];
  matches: TournamentMatch[];
  myParticipantId: number;
}): MyPath | null {
  const { myParticipantId } = input;
  const byId = new Map(input.matches.map((match) => [match.id, match]));
  const steps: PathStep[] = [];
  const matchIds = new Set<number>();
  let alive = true;
  let wins = 0;
  let found = false;
  for (const round of input.rounds) {
    const mineInRound = round.matches
      .map((bracketMatch) => (bracketMatch.matchId != null ? byId.get(bracketMatch.matchId) ?? null : null))
      .find((match) => !!match && hasParticipant(match, myParticipantId)) ?? null;
    if (mineInRound) {
      found = true;
      matchIds.add(mineInRound.id);
      const opponent = opponentOf(mineInRound, myParticipantId);
      if (isDecided(mineInRound)) {
        const won = mineInRound.winner_participant_id === myParticipantId;
        if (won) wins += 1;
        else alive = false;
        steps.push({
          label: round.label,
          match: mineInRound,
          state: won ? "won" : "lost",
          opponent,
          score: scoreFromSide(mineInRound, myParticipantId),
          when: null,
        });
      } else {
        steps.push({ label: round.label, match: mineInRound, state: "next", opponent, score: null, when: playerWhen(mineInRound) });
      }
      if (!alive) break;
      continue;
    }
    if (found && alive) {
      // Ronda futura: todavía sin tu partido (o ya generada sin ti en ella).
      const first = round.matches[0]?.matchId != null ? byId.get(round.matches[0].matchId as number) ?? null : null;
      steps.push({
        label: round.label,
        match: null,
        state: "future",
        opponent: null,
        score: null,
        when: round.matches.length === 1 && first ? playerWhen(first) : null,
      });
    }
  }
  if (!found) return null;
  const nextStep = steps.find((step) => step.state === "next") ?? null;
  const lastPlayed = [...steps].reverse().find((step) => step.state === "won" || step.state === "lost") ?? null;
  return {
    steps,
    matchIds,
    alive,
    wins,
    currentLabel: nextStep?.label ?? (alive ? steps.find((step) => step.state === "future")?.label ?? lastPlayed?.label ?? null : lastPlayed?.label ?? null),
  };
}

export function participantName(brief: MatchParticipantBrief | null | undefined): string {
  return matchParticipantShortName(brief ?? null);
}
